import React, { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, RefreshCw, Pause, Play, Trash2, Upload, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const PORTAL_LABELS = {
  chaves_na_mao: 'Chaves na Mão',
  olx: 'OLX',
  sub100: 'Sub100',
  zap: 'Zap Imóveis',
  viva_real: 'Viva Real',
};

const STATUS_CONFIG = {
  nao_publicado: { label: 'Não publicado', cor: 'bg-gray-100 text-gray-500', dot: 'bg-gray-300' },
  publicando: { label: 'Publicando...', cor: 'bg-yellow-50 text-yellow-700', dot: 'bg-yellow-400 animate-pulse' },
  publicado: { label: 'Publicado', cor: 'bg-green-50 text-green-700', dot: 'bg-green-500' },
  atualizando: { label: 'Atualizando...', cor: 'bg-yellow-50 text-yellow-700', dot: 'bg-yellow-400 animate-pulse' },
  removendo: { label: 'Removendo...', cor: 'bg-red-50 text-red-500', dot: 'bg-red-300 animate-pulse' },
  removido: { label: 'Removido', cor: 'bg-gray-100 text-gray-400', dot: 'bg-gray-300' },
  erro: { label: 'Erro', cor: 'bg-red-50 text-red-700', dot: 'bg-red-500' },
  pausado: { label: 'Pausado', cor: 'bg-orange-50 text-orange-700', dot: 'bg-orange-400' },
};

const TRANSITORIO = ['publicando', 'atualizando', 'removendo'];

function formatDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export default function PortaisPanel({ imovelId }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const intervalRef = useRef(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['imovel-portais', imovelId],
    queryFn: () => api.get(`/imoveis/${imovelId}/portais`).then(r => r.data.data),
    refetchInterval: false,
  });

  const portais = data || [];
  const temTransitorio = portais.some(p => TRANSITORIO.includes(p.status));

  // Polling automático quando há portais em estado transitório
  useEffect(() => {
    if (temTransitorio) {
      intervalRef.current = setInterval(() => refetch(), 5000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [temTransitorio, refetch]);

  const mutPublicar = useMutation({
    mutationFn: (portaisAlvo) => api.post(`/imoveis/${imovelId}/publicar`, { portais: portaisAlvo }),
    onSuccess: () => qc.invalidateQueries(['imovel-portais', imovelId]),
  });

  const mutAtualizar = useMutation({
    mutationFn: (portal) => api.put(`/imoveis/${imovelId}/publicar`, { portais: [portal] }),
    onSuccess: () => qc.invalidateQueries(['imovel-portais', imovelId]),
  });

  const mutRemover = useMutation({
    mutationFn: (portaisAlvo) => api.delete(`/imoveis/${imovelId}/publicar`, { data: { portais: portaisAlvo } }),
    onSuccess: () => qc.invalidateQueries(['imovel-portais', imovelId]),
  });

  const mutPausar = useMutation({
    mutationFn: (portal) => api.patch(`/imoveis/${imovelId}/portais/${portal}/pausar`),
    onSuccess: () => qc.invalidateQueries(['imovel-portais', imovelId]),
  });

  const mutReativar = useMutation({
    mutationFn: (portal) => api.patch(`/imoveis/${imovelId}/portais/${portal}/reativar`),
    onSuccess: () => qc.invalidateQueries(['imovel-portais', imovelId]),
  });

  const publicados = portais.filter(p => p.status === 'publicado');

  if (isLoading) return <div className="p-4 text-gray-400 text-sm">Carregando portais...</div>;

  return (
    <div className="space-y-4">
      {/* Ações globais */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => mutPublicar.mutate(null)}
          disabled={mutPublicar.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-secondary disabled:opacity-50"
        >
          <Upload size={14} /> Publicar em todos
        </button>
        {publicados.length > 0 && (
          <>
            <button
              onClick={() => mutAtualizar.mutate(publicados.map(p => p.portal))}
              disabled={mutAtualizar.isPending}
              className="flex items-center gap-2 px-4 py-2 border border-blue-500 text-blue-600 rounded-lg text-sm hover:bg-blue-50 disabled:opacity-50"
            >
              <RefreshCw size={14} /> Atualizar todos
            </button>
            <button
              onClick={() => { if (window.confirm('Remover de todos os portais?')) mutRemover.mutate(null); }}
              disabled={mutRemover.isPending}
              className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-500 rounded-lg text-sm hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 size={14} /> Remover de todos
            </button>
          </>
        )}
      </div>

      {/* Grid de cards por portal */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {portais.map(p => {
          const cfg = STATUS_CONFIG[p.status] || STATUS_CONFIG.nao_publicado;
          return (
            <div key={p.portal} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm space-y-3">
              {/* Header */}
              <div className="flex items-center justify-between">
                <span className="font-semibold text-gray-800 text-sm">{PORTAL_LABELS[p.portal]}</span>
                <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.cor}`}>
                  <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                  {cfg.label}
                </div>
              </div>

              {/* Link do anúncio */}
              {p.listing_url && (
                <a
                  href={p.listing_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                >
                  <ExternalLink size={12} /> Ver anúncio
                </a>
              )}

              {/* Erro */}
              {p.status === 'erro' && p.ultimo_erro && (
                <p className="text-xs text-red-600 bg-red-50 rounded p-2 break-all">{p.ultimo_erro}</p>
              )}

              {/* Último sync */}
              {p.ultimo_sync && (
                <p className="text-xs text-gray-400">Sync: {formatDate(p.ultimo_sync)}</p>
              )}

              {/* Não configurado */}
              {!p.configurado && (
                <p className="text-xs text-amber-600 bg-amber-50 rounded p-2">
                  API Key não configurada.{' '}
                  <button onClick={() => navigate('/configuracoes/integracoes')} className="underline">Configurar</button>
                </p>
              )}

              {/* Ações */}
              <div className="flex gap-2 flex-wrap">
                {(p.status === 'nao_publicado' || p.status === 'removido') && (
                  <button
                    onClick={() => mutPublicar.mutate([p.portal])}
                    disabled={mutPublicar.isPending || !p.configurado}
                    className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-lg text-xs hover:bg-secondary disabled:opacity-40"
                  >
                    <Upload size={12} /> Publicar
                  </button>
                )}

                {p.status === 'publicado' && (
                  <>
                    <button
                      onClick={() => mutAtualizar.mutate(p.portal)}
                      disabled={mutAtualizar.isPending}
                      className="flex items-center gap-1 px-3 py-1.5 border border-blue-400 text-blue-600 rounded-lg text-xs hover:bg-blue-50 disabled:opacity-40"
                    >
                      <RefreshCw size={12} /> Atualizar
                    </button>
                    <button
                      onClick={() => mutPausar.mutate(p.portal)}
                      disabled={mutPausar.isPending}
                      className="flex items-center gap-1 px-3 py-1.5 border border-orange-400 text-orange-600 rounded-lg text-xs hover:bg-orange-50 disabled:opacity-40"
                    >
                      <Pause size={12} /> Pausar
                    </button>
                    <button
                      onClick={() => { if (window.confirm(`Remover de ${PORTAL_LABELS[p.portal]}?`)) mutRemover.mutate([p.portal]); }}
                      disabled={mutRemover.isPending}
                      className="flex items-center gap-1 px-3 py-1.5 border border-red-300 text-red-500 rounded-lg text-xs hover:bg-red-50 disabled:opacity-40"
                    >
                      <Trash2 size={12} /> Remover
                    </button>
                  </>
                )}

                {p.status === 'erro' && (
                  <>
                    <button
                      onClick={() => mutPublicar.mutate([p.portal])}
                      disabled={mutPublicar.isPending}
                      className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-lg text-xs hover:bg-secondary disabled:opacity-40"
                    >
                      <RefreshCw size={12} /> Tentar novamente
                    </button>
                    <button
                      onClick={() => mutRemover.mutate([p.portal])}
                      disabled={mutRemover.isPending}
                      className="flex items-center gap-1 px-3 py-1.5 border border-red-300 text-red-500 rounded-lg text-xs hover:bg-red-50 disabled:opacity-40"
                    >
                      <Trash2 size={12} /> Remover
                    </button>
                  </>
                )}

                {p.status === 'pausado' && (
                  <>
                    <button
                      onClick={() => mutReativar.mutate(p.portal)}
                      disabled={mutReativar.isPending}
                      className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs hover:bg-green-700 disabled:opacity-40"
                    >
                      <Play size={12} /> Reativar
                    </button>
                    <button
                      onClick={() => mutRemover.mutate([p.portal])}
                      disabled={mutRemover.isPending}
                      className="flex items-center gap-1 px-3 py-1.5 border border-red-300 text-red-500 rounded-lg text-xs hover:bg-red-50 disabled:opacity-40"
                    >
                      <Trash2 size={12} /> Remover
                    </button>
                  </>
                )}

                {!p.configurado && (
                  <button
                    onClick={() => navigate('/configuracoes/integracoes')}
                    className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-500 rounded-lg text-xs hover:bg-gray-50"
                  >
                    <Settings size={12} /> Configurar
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
